/**
 * Recipe Service - Supabase Integration
 * Handles all recipe CRUD operations with Supabase
 */

import { supabase } from './supabaseClient';

const resolveMemberId = async (memberEmail) => {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  const authId = authData?.user?.id;

  if (authId) {
    const { data: memberByAuth, error: memberByAuthError } = await supabase
      .from('members')
      .select('id')
      .eq('auth_id', authId)
      .maybeSingle();

    if (memberByAuthError) throw memberByAuthError;
    if (memberByAuth?.id) return memberByAuth.id;
  }

  if (memberEmail) {
    const { data: memberByEmail, error: memberByEmailError } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .maybeSingle();

    if (memberByEmailError) throw memberByEmailError;
    if (memberByEmail?.id) return memberByEmail.id;
  }

  throw new Error('Member not found');
};

/**
 * Get all public recipes
 */
export const getPublicRecipes = async () => {
  try {
    const { data, error } = await supabase
      .from('recipes')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Transform Supabase format to match what MemberRecipes expects
    const transformedData = (data || []).map(recipe => ({
      id: recipe.id,
      title: recipe.title,
      image: recipe.image_url || '🍽️',
      prepTime: recipe.prep_time || '',
      cookTime: recipe.cook_time || '',
      servings: recipe.servings || 1,
      difficulty: recipe.difficulty?.charAt(0).toUpperCase() + recipe.difficulty?.slice(1) || 'Easy',
      rating: recipe.rating || 4.5,
      reviews: recipe.reviews || 0,
      mealType: recipe.meal_type || recipe.category || 'dinner',
      dietary: Array.isArray(recipe.allergen_free) ? recipe.allergen_free : [],
      nutritionHighlights: Array.isArray(recipe.tags) ? recipe.tags : [],
      ingredients: recipe.ingredients || [],
      instructions: recipe.instructions || [],
      description: recipe.description || '',
      calories: recipe.nutrition?.calories || null,
      protein: recipe.nutrition?.protein || null,
      carbs: recipe.nutrition?.carbs || null,
      fats: recipe.nutrition?.fats || null
    }));
    
    return { success: true, data: transformedData };
  } catch (error) {
    console.error('Error fetching recipes:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get a single recipe by ID
 */
export const getRecipeById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('recipes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching recipe:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new recipe
 */
export const createRecipe = async (recipeData) => {
  try {
    // Get current user's member ID from email
    const userEmail = localStorage.getItem('userEmail');
    let memberId = null;

    if (userEmail) {
      const { data: member } = await supabase
        .from('members')
        .select('id')
        .eq('email', userEmail)
        .single();
      
      memberId = member?.id;
    }

    const { data, error } = await supabase
      .from('recipes')
      .insert([{
        ...recipeData,
        created_by: memberId,
        is_public: true
      }])
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error creating recipe:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing recipe
 */
export const updateRecipe = async (id, recipeData) => {
  try {
    console.log('Updating recipe with ID:', id);
    console.log('Recipe data being sent:', recipeData);
    
    const { data, error } = await supabase
      .from('recipes')
      .update(recipeData)
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      console.error('Supabase update error:', error);
      throw error;
    }
    
    console.log('Update successful, returned data:', data);
    
    return { success: true, data };
  } catch (error) {
    console.error('Error updating recipe:', error);
    console.error('Error details:', JSON.stringify(error, null, 2));
    return { success: false, error: error.message || 'Unknown error' };
  }
};

/**
 * Delete a recipe
 */
export const deleteRecipe = async (id) => {
  try {
    const { error } = await supabase
      .from('recipes')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting recipe:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Search recipes by filters
 */
export const searchRecipes = async (filters = {}) => {
  try {
    let query = supabase
      .from('recipes')
      .select('*')
      .eq('is_public', true);

    // Filter by category (check both meal_type and category for compatibility)
    if (filters.category && filters.category !== 'all') {
      query = query.or(`meal_type.eq.${filters.category},category.eq.${filters.category}`);
    }

    // Filter by difficulty
    if (filters.difficulty && filters.difficulty !== 'all') {
      query = query.eq('difficulty', filters.difficulty);
    }

    // Filter by allergen-free
    if (filters.allergenFree && filters.allergenFree.length > 0) {
      query = query.contains('allergen_free', filters.allergenFree);
    }

    // Search by title
    if (filters.search) {
      query = query.ilike('title', `%${filters.search}%`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error searching recipes:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get member's favorite recipes
 */
export const getFavoriteRecipes = async (memberEmail) => {
  try {
    const memberId = await resolveMemberId(memberEmail);

    // Get favorite recipe IDs
    const { data: favorites, error: favError } = await supabase
      .from('member_favorites')
      .select('item_id')
      .eq('member_id', memberId)
      .eq('item_type', 'recipe');

    if (favError) throw favError;

    if (!favorites || favorites.length === 0) {
      return { success: true, data: [] };
    }

    // Get the actual recipes
    const recipeIds = favorites.map(f => f.item_id);
    const { data: recipes, error: recipeError } = await supabase
      .from('recipes')
      .select('*')
      .in('id', recipeIds);

    if (recipeError) throw recipeError;
    return { success: true, data: recipes || [] };
  } catch (error) {
    console.error('Error fetching favorite recipes:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Add recipe to favorites
 */
export const addToFavorites = async (memberEmail, recipeId) => {
  try {
    const memberId = await resolveMemberId(memberEmail);

    const { error } = await supabase
      .from('member_favorites')
      .insert([{
        member_id: memberId,
        item_type: 'recipe',
        item_id: recipeId
      }]);

    if (error) {
      const message = (error && (error.message || error.details)) || '';
      if (message.toLowerCase().includes('duplicate')) return { success: true };
      throw error;
    }
    return { success: true };
  } catch (error) {
    console.error('Error adding to favorites:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove recipe from favorites
 */
export const removeFromFavorites = async (memberEmail, recipeId) => {
  try {
    const memberId = await resolveMemberId(memberEmail);

    const { error } = await supabase
      .from('member_favorites')
      .delete()
      .eq('member_id', memberId)
      .eq('item_type', 'recipe')
      .eq('item_id', recipeId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error removing from favorites:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get popular recipes (most favorited)
 */
export const getPopularRecipes = async (limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('popular_recipes')
      .select('*')
      .limit(limit);

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching popular recipes:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export default {
  getPublicRecipes,
  getRecipeById,
  createRecipe,
  updateRecipe,
  deleteRecipe,
  searchRecipes,
  getFavoriteRecipes,
  addToFavorites,
  removeFromFavorites,
  getPopularRecipes
};
